// Copyright 2017 Sorama B.V.

import { DialogService } from "aurelia-dialog";
import { Subscription } from "aurelia-event-aggregator";
import { autoinject, BindingEngine, PLATFORM } from "aurelia-framework";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { RecordingContainer, RecordingGroupContainer } from "datatypes/Containers";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ConfirmationModel } from "modules/dialogs/Confirmation/ConfirmationDialog";
import { CreditsBalanceRepository } from "repositories/CreditsBalanceRepository";
import { IMeasurementStorageService, IMeasurementUploadStatus } from "services/Measurement/IMeasurementStorageService";
import { MeasurementCollectionService } from "services/Measurement/MeasurementCollectionService";
import { Measurement } from "services/Measurement/MeasurementTypes";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { IRecordingGroupContainerStorageService } from "services/Storage/IRecordingGroupContainerStorageService";
import { IStorageService, ITypedStorageContainerSet, StorageTypes } from "services/Storage/IStorageService";
import { StorageServiceExtensions } from "services/Storage/StorageServiceExtensions";
import { Time } from "units/Time";
import { Unit } from "units/Unit";

/**
 * MODULE OPERATION DESCRIPTION - MeasurementModule
 * The MeasurementModule lists all details of a recording group or cloud measurement
 */
@autoinject
export class MeasurementModule implements IWorkflowModuleLifetime {
    //#region Constants
    public milliseconds: Unit = Time.milliSecond;
    public seconds: Unit = Time.second;
    //#endregion

    //#region View Properties
    private recordingGroupContainerSet: ITypedStorageContainerSet;
    private recordingsContainerSet: ITypedStorageContainerSet;

    private currentGroupId: string;
    private group: RecordingGroupContainer;
    private measurement: Measurement;
    private measurementStorageInformation: IMeasurementUploadStatus;
    private storageLocation: string;
    private recordings: RecordingContainer[];
    private subscriptions: Subscription[] = [];
    private distanceStep: number;
    private size: number;
    private creditsSpent: number;

    private moduleInstance: IWorkflowModuleInstance;

    //#endregion

    //#region Workflow Properties
    private selectedGroupId: string;

    private selectedRecordingId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, changedHandler: "measurementChanged" })
    private selectedMeasurementId: string;
    //#endregion

    //#region Private Properties
    private bindPromise: Promise<void>;
    private refreshPromise: Promise<void> = Promise.resolve();
    //#endregion

    //#region Lifecycle

    constructor(
        private readonly storageService: IStorageService,
        private readonly dialogService: DialogService,
        private readonly recordingGroupContainerStorageService: IRecordingGroupContainerStorageService,
        private readonly measurementCollectionService: MeasurementCollectionService,
        private readonly measurementStorageService: IMeasurementStorageService,
        private readonly creditsBalanceRepository: CreditsBalanceRepository,
        private readonly authentication: IAuthentication,
        private readonly projectManagementService: IProjectManagementService,
        private readonly bindingEngine: BindingEngine
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        this.bindPromise = Promise.all([
            this.storageService.getTypedStorageContainerSet(StorageTypes.recording).then((set) => {
                this.recordingsContainerSet = set;
                this.subscriptions.push(set);
            }),
            this.storageService.getTypedStorageContainerSet(StorageTypes.recordingGroup).then((set) => {
                this.recordingGroupContainerSet = set;
                this.subscriptions.push(set);
            }),
        ]).then(() => {
            this.subscriptions.push(
                this.bindingEngine
                    .collectionObserver(this.recordingsContainerSet.containers)
                    .subscribe(this.onContainersChanged)
            );

            this.subscriptions.push(
                this.bindingEngine
                    .collectionObserver(this.recordingGroupContainerSet.containers)
                    .subscribe(this.onContainersChanged)
            );

            this.selectedGroupIdChanged(this.selectedGroupId);

            if (this.selectedRecordingId) {
                const id = this.selectedRecordingId;
                this.selectedRecordingId = undefined;
                this.toggleRecording(id);
            }
        });
    }

    public bindMeasurement(): void {
        if (this.selectedMeasurementId) {
            this.measurement = this.measurementCollectionService.measurements.find(
                (m) => m.id === this.selectedMeasurementId
            );

            // Set sor path if sor measurement
            if (this.measurement.sorId) {
                this.storageService.getSorPath(this.measurement.sorId).then((path) => {
                    this.storageLocation = path;
                });
            }

            if (this.measurement.isIncomplete) {
                this.selectedGroupId = this.measurement.containerId;
            } else {
                // Set credit spent on this measurement
                this.getMeasurementCreditSpent(this.measurement.id);

                // Check if the measurement has a cloud data location before attempting to get storage information
                if (this.measurement.dataLocation.has("Cloud")) {
                    this.measurementStorageService
                        .getStorageInformation(this.selectedMeasurementId)
                        .then((x) => (this.measurementStorageInformation = x));
                }

                if (this.measurement.dataLocation.has("Local") && this.measurement.containerId) {
                    this.selectedGroupId = this.measurement.containerId;
                } else {
                    this.selectedGroupId = undefined;
                }
            }
        }
    }

    public deactivateModule(): void {
        this.selectedRecordingId = undefined;

        this.bindPromise.then(() => {
            while (this.subscriptions.length > 0) this.subscriptions.pop().dispose();
        });
    }

    //#endregion

    //#region Property Changed Callbacks
    public measurementChanged(): void {
        this.bindMeasurement();
        this.refresh();
    }

    public selectedGroupIdChanged(newValue: string): void {
        this.toggleRecording(this.selectedRecordingId);

        this.refresh();
    }

    private onContainersChanged = (): void => {
        this.refresh();
    };

    //#endregion

    public editMeasurement(): void {
        this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/EditMeasurement/EditMeasurementDialog"),
            model: this.measurement.id,
            lock: true,
        });
    }

    public deleteMeasurement(): void {
        const confirmationModel: ConfirmationModel = {
            title: "Delete Measurement",
            text: "Are you sure you want to delete this measurement? All recorded data will also be deleted and this action cannot be undone!",
            confirmText: "Delete",
            operation: async () => {
                this.measurement.delete(this.projectManagementService.currentProject.item.id).then(() => {
                    // close opened measurement info modules
                    const recordingModules = this.moduleInstance
                        .getWorkflow()
                        .getModules(MeasurementWorkflowModuleCatalog.MeasurementModule);
                    for (const module of recordingModules) module.close();
                    this.measurementCollectionService.refreshAllMeasurements();
                });
            },
        };

        this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
            model: confirmationModel,
            lock: true,
        });
    }

    public toggleRecording(recordingId: string): void {
        if (this.selectedRecordingId === recordingId) {
            this.selectedRecordingId = undefined;
        } else this.selectedRecordingId = recordingId;
    }

    public deleteRecording(recording: RecordingContainer): void {
        this.moduleInstance
            .createNotification()
            .withMessage("Delete recording '" + recording.metadata.name + "'? This action cannot be undone!")
            .withButton("Yes", () => {
                this.recordingGroupContainerStorageService.deleteRecording(this.selectedGroupId, recording.id);
            })
            .withButton("No")
            .show();
    }

    public showMissingRecordingActions(missingRecordingId: string): void {
        this.moduleInstance
            .createNotification()
            .withMessage(
                "The recording with id '" +
                    missingRecordingId +
                    "' could not be found." +
                    " If you don't have the recordings raw data, you can remove it from this group." +
                    " If you have the recordings raw data, make sure it is located in the correct folder."
            )
            .withButton("Remove", () => {
                this.removeRecordingFromGroup(missingRecordingId);
            })
            .withButton("Open Folder", () => () => {
                const containerPath = this.storageLocation.slice(
                    0,
                    this.storageLocation.length - this.selectedGroupId.length - 1
                );
                this.storageService.openInExplorer(containerPath);
            })
            .withButton("Do Nothing")
            .show();
    }
    //#endregion

    //#region Helper Methods
    private refresh(): Promise<void> {
        if (!this.recordingsContainerSet || !this.selectedGroupId) return;

        this.refreshPromise = this.refreshPromise
            .then(() => {
                if (!this.recordingGroupContainerSet) return;

                const group = <RecordingGroupContainer>(
                    this.recordingGroupContainerSet.containers.find((x) => x.id === this.selectedGroupId)
                );
                if (!group) return;

                // Set current group
                this.group = group;

                // Set file size
                this.storageService.getRecordingGroupSize(group.id).then((size) => (this.size = size));

                this.recordings = <RecordingContainer[]>this.recordingsContainerSet.containers
                    .filter((x) => this.group.metadata.recordings.indexOf(x.id) != -1)
                    .sort((a: RecordingContainer, b: RecordingContainer) => {
                        // Sort based on position in scan
                        const aPos = group.metadata.steps.findIndex((x) => x.recordingId === a.id);
                        const bPos = group.metadata.steps.findIndex((x) => x.recordingId === b.id);

                        if (aPos === -1) return 1;
                        if (bPos === -1) return -1;

                        return aPos - bPos;
                    });

                if (this.measurement.distance) {
                    if (this.measurement.distance < Number.EPSILON) this.distanceStep = 0.001;
                    else this.distanceStep = Math.pow(10, Math.floor(Math.log10(this.measurement.distance)));
                }

                if (this.currentGroupId !== this.group.id) {
                    this.storageLocation = undefined;
                    return this.storageService.getPath(this.selectedGroupId).then((path) => {
                        this.storageLocation = path;
                        this.currentGroupId = this.group.id;
                    });
                }
            })
            .catch((e: Error) => {
                this.moduleInstance
                    .createNotification()
                    .withUrgency(NotificationUrgency.Error)
                    .withMessage(e.message)
                    .show();
            });

        return this.refreshPromise;
    }

    private getMeasurementCreditSpent(measurementId: string): void {
        const request = this.creditsBalanceRepository.getMeasurementCreditSpent(
            this.authentication.businessUnitId,
            measurementId
        );
        request.then((credit) => (this.creditsSpent = credit));
    }

    private removeRecordingFromGroup(recordingId: string): void {
        StorageServiceExtensions.removeRecordingFromGroup(this.storageService, recordingId, this.selectedGroupId);
    }

    //#endregion

    //#region Helper Methods
    public openPathInExplorer(path: string): void {
        this.storageService.openInExplorer(path);
    }
    //#endregion

    public getStorageLocationString(measurement: Measurement): string {
        const storageLocations = [];
        if (measurement.dataLocation.has("Cloud")) storageLocations.push("Cloud");

        if (measurement.dataLocation.has("Local")) {
            if (measurement.origin.has("Local")) {
                storageLocations.push("Local (On this computer)");
            } else {
                if (measurement.owner) {
                    const ownerName = measurement.owner.LastName
                        ? measurement.owner.FirstName + " " + measurement.owner.LastName
                        : measurement.owner.FirstName;
                    storageLocations.push(`Local (On the computer of ${ownerName})`);
                } else {
                    storageLocations.push("Local (On someone else's computer, the owner is unknown)");
                }
            }
        }
        return storageLocations.join(" & ");
    }
}
